import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AuthState } from '../../../auth/store/auth.model';
import { selectUser } from '../../../auth/store/auth.selector';
import { ProfileApiService } from '../../../pages/profile/profile-api.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-education-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './education-details.component.html',
  styleUrl: './education-details.component.css'
})
export class EducationDetailsComponent implements OnInit {
  private readonly degreeMaxLength = 100;
  private readonly titleMaxLength = 100;
  private readonly instituteMaxLength = 100;
  private readonly yearMin = 1900;
  readonly currentYear = new Date().getFullYear();
  openSection: string | null = null;
  educationForm: FormGroup;
  userId: number | string | null = null;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private store: Store<AuthState>,
    private profileApi: ProfileApiService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {
    this.educationForm = this.fb.group({
      tenth: this.createEducationGroup({ degree: 'SSC' }),
      twelfth: this.createEducationGroup({ degree: 'HSC' }),
      graduation: this.createEducationGroup(),
      postGraduation: this.createEducationGroup(),
      professionalCoursesCompleted: [false],
      professionalCourses: this.fb.array([this.createProfessionalCourse()])
    });
    this.configureStandardCompletedToggles();
    this.configureProfessionalCoursesCompletedToggle();
  }
  ngOnInit() {
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        const masterUserId = localStorage.getItem('master_user_id') ?? user?.id ?? this.getUserIdFromStorage();
        if (masterUserId) {
          this.userId = masterUserId;
          this.loadEducation(masterUserId);
        }
      }
    });
  }

  get tenth(): FormGroup {
    return this.educationForm.get('tenth') as FormGroup;
  }

  get twelfth(): FormGroup {
    return this.educationForm.get('twelfth') as FormGroup;
  }

  get graduation(): FormGroup {
    return this.educationForm.get('graduation') as FormGroup;
  }

  get postGraduation(): FormGroup {
    return this.educationForm.get('postGraduation') as FormGroup;
  }

  get professionalCourses(): FormArray {
    return this.educationForm.get('professionalCourses') as FormArray;
  }

  get professionalCoursesCompleted(): AbstractControl | null {
    return this.educationForm.get('professionalCoursesCompleted');
  }

  toggleSection(section: string, event: Event) {
    event.preventDefault();
    if (section === 'professionalCourses' && !this.professionalCoursesEnabled()) {
      this.openSection = null;
      return;
    }

    if (section !== 'professionalCourses' && !this.getStandardSection(section)?.get('completed')?.value) {
      this.openSection = null;
      return;
    }

    this.openSection = this.openSection === section ? null : section;
  }

  isSectionOpen(section: string): boolean {
    if (section === 'professionalCourses') {
      return this.openSection === section && this.professionalCoursesEnabled();
    }

    return this.openSection === section && !!this.getStandardSection(section)?.get('completed')?.value;
  }

  onStandardCompletedChange(section: string) {
    const group = this.getStandardSection(section);
    if (!group) {
      return;
    }

    this.applyStandardCompletedValidators(group, this.requiresDegree(section));
    this.openSection = group.get('completed')?.value ? section : null;
  }

  onProfessionalCoursesCompletedChange() {
    this.applyProfessionalCoursesCompletedValidators();
    this.openSection = this.professionalCoursesEnabled() ? 'professionalCourses' : null;
  }

  addProfessionalCourse() {
    this.professionalCourses.push(this.createProfessionalCourse({
      title: '',
      year: '',
      institute: ''
    }));
  }

  removeProfessionalCourse(index: number, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (index > 0 && index < this.professionalCourses.length) {
      this.professionalCourses.removeAt(index);
      this.professionalCourses.markAsDirty();
      this.professionalCourses.updateValueAndValidity();
    }
  }

  showError(control: AbstractControl | null): boolean {
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  getPercentageError(control: AbstractControl | null): string {
    if (!control) {
      return '';
    }

    if (control.hasError('required')) {
      return 'Percentage is required';
    }

    if (control.hasError('min')) {
      return 'Percentage cannot be less than 0';
    }

    if (control.hasError('max')) {
      return 'Percentage cannot be more than 100';
    }

    return '';
  }

  getYearError(control: AbstractControl | null, label: string): string {
    if (!control) {
      return '';
    }

    if (control.hasError('required')) {
      return `${label} is required`;
    }

    if (control.hasError('min')) {
      return `${label} must be ${this.yearMin} or later`;
    }

    if (control.hasError('max')) {
      return `${label} must be before or in current year`;
    }

    return ''
  }

  getTextError(control: AbstractControl | null, label: string): string {
    if (!control) {
      return '';
    }

    if (control.hasError('required')) {
      return `${label} is required`;
    }

    if (control.hasError('minlength')) {
      return `${label} must be at least ${control.getError('minlength')?.requiredLength} characters`;
    }

    if (control.hasError('maxlength')) {
      return `${label} cannot exceed ${control.getError('maxlength')?.requiredLength} characters`;
    }

    return '';
  }

  saveEducation() {
    if (!this.isEducationFormValid()) {
      this.educationForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', this.getValidationErrorMessage());
      return;
    }

    // All-or-nothing section validation (UI rule)
    // If user starts filling any standard/higher/professional record, all required fields must be filled.
    if (!this.validateAllOrNothingEducationRecords()) {
      this.educationForm.markAllAsTouched();
      this.toasterService.setToaster(
        'error',
        'Validation Error',
        'Complete all required fields for each education section marked Completed.'
      );
      return;
    }

    if (!this.userId) {
      this.toasterService.setToaster('error', 'Error', 'User ID not found.');
      return;
    }

    const payload = this.buildEducationPayload();
    if (!payload.length) {
      this.educationForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fill at least one education detail before saving.');
      return;
    }

    this.isSaving = true;
    this.profileApi.createUserEducation(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res?.status === 'success' || res?.status === 200 || res?.code === 200) {
          this.toasterService.setToaster('success', 'Success', res.message ?? 'Education details saved successfully');
          const savedList = this.extractEducationList(res);
          if (savedList.length) {
            this.patchEducation(savedList);
          } else {
            this.loadEducation(this.userId!);
          }
        } else {
          const message = res?.message || 'Failed to save education details';
          this.toasterService.setToaster('error', 'Error', message);
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        const apiMessage = err?.error?.message || err?.error?.error || err?.message;
        if (apiMessage?.toString().includes('User ID is required')) {
          this.educationForm.markAllAsTouched();
          this.toasterService.setToaster('error', 'Validation Error', 'Please fill at least one education detail before saving.');
          return;
        }

        this.toasterService.setToaster('error', 'Failed', apiMessage || 'Unable to save education details');
      }
    });
  }

  private validateAllOrNothingEducationRecords(): boolean {
    const tenthVal = this.tenth.getRawValue();
    const twelfthVal = this.twelfth.getRawValue();
    const graduationVal = this.graduation.getRawValue();
    const postGraduationVal = this.postGraduation.getRawValue();

    // If a section is marked Completed, enforce all required fields for that section.
    if (this.isStartedStandardSection(tenthVal) && !this.isCompleteStandardSection(tenthVal)) return false;
    if (this.isStartedStandardSection(twelfthVal) && !this.isCompleteStandardSection(twelfthVal)) return false;
    if (this.isStartedStandardSection(graduationVal) && !this.isCompleteStandardSection(graduationVal, true)) return false;
    if (this.isStartedStandardSection(postGraduationVal) && !this.isCompleteStandardSection(postGraduationVal, true)) return false;

    if (this.professionalCoursesEnabled()) {
      for (const control of this.professionalCourses.controls) {
        const courseVal = control.getRawValue();
        if (!this.isCompleteProfessionalCourseSection(courseVal)) {
          return false;
        }
      }
    }

    return true;
  }

  private professionalCoursesEnabled(): boolean {
    return !!this.professionalCoursesCompleted?.value;
  }

  private applyProfessionalCoursesCompletedValidators() {
    if (!this.professionalCourses.length) {
      this.professionalCourses.push(this.createProfessionalCourse());
    }

    this.professionalCourses.controls.forEach((control) => {
      this.applyProfessionalCourseCompletedValidators(control as FormGroup);
    });

    this.professionalCourses.updateValueAndValidity({ emitEvent: false });
  }

  private ensureProfessionalCoursesEnabledFromCourses() {
    const hasCourses = this.professionalCourses.controls.some((control) => {
      const course = control.getRawValue();
      return this.hasProfessionalCourseData(course);
    });

    this.professionalCoursesCompleted?.setValue(hasCourses, { emitEvent: false });
    this.applyProfessionalCoursesCompletedValidators();

    if (!hasCourses && this.openSection === 'professionalCourses') {
      this.openSection = null;
    }
  }

  private areProfessionalCoursesValid(): boolean {
    if (!this.professionalCoursesEnabled()) {
      return true;
    }

    if (!this.professionalCourses.length) {
      return false;
    }

    return !this.professionalCourses.controls.some((control) => {
      const courseVal = control.getRawValue();
      return !this.isCompleteProfessionalCourseSection(courseVal) || control.invalid;
    });
  }

  private isStartedStandardSection(sectionValue: any): boolean {
    return !!sectionValue?.completed;
  }

  private buildEducationPayload() {

    const value = this.educationForm.getRawValue();
    const payload: Record<string, unknown>[] = [];

    // Only send SSC (10th) / HSC (12th) record if ALL required UI inputs are present:
    // - if percentage is empty => do not send this section at all
    // - if year_of_degree OR institute_name is empty => do not send this section at all
    if (this.isCompleteStandardSection(value.tenth)) {
      payload.push(this.mapEducationRecord('ssc', value.tenth, { defaultDegree: 'SSC' }));
    }
    if (this.isCompleteStandardSection(value.twelfth)) {
      payload.push(this.mapEducationRecord('hsc', value.twelfth, { defaultDegree: 'HSC' }));
    }

    // Graduation / Post-graduation rules:
    // If any required field is empty, do not send that record at all.
    if (this.isCompleteStandardSection(value.graduation, true)) {
      payload.push(this.mapEducationRecord('graduation', value.graduation));
    }
    if (this.isCompleteStandardSection(value.postGraduation, true)) {
      payload.push(this.mapEducationRecord('post-graduation', value.postGraduation));
    }




    if (this.professionalCoursesEnabled()) {
      this.professionalCourses.controls.forEach((control) => {
        const course = control.getRawValue();
        // Professional course also should be all-or-nothing:
        // if any of title/year/institute is empty, do not send that record.
        if (this.isCompleteProfessionalCourseSection(course)) {
          payload.push(this.mapProfessionalCourse(course));
        }
      });
    }


    return payload;
  }


  private mapEducationRecord(
    educationType: string,
    education: any,
    options?: { defaultDegree?: string }
  ) {
    const yearStr = this.toPayloadString(education?.year_of_degree);
    const instituteStr = this.toPayloadString(education?.institute_name);
    const percentageStr = this.toPayloadString(education?.percentage);

    // Important: backend must not receive empty fields for missing values.
    // Also, if percentage is missing, year/institute should not be sent.
    const includeYearInstitute = !!percentageStr;

    const item: Record<string, unknown> = {
      user_id: this.userId,
      education_type: educationType,
      degree: education?.degree || options?.defaultDegree || '',
      Title: null,
      CompletedStatus: education?.completed ? 1 : 0,
      is_active: true,
      ...(percentageStr ? { Percentage: percentageStr } : {}),
      ...(includeYearInstitute && yearStr ? { year_of_degree: yearStr } : {}),
      ...(includeYearInstitute && instituteStr ? { institute_name: instituteStr } : {})
    };

    this.appendIdIfExists(item, education?.id);
    return item;
  }


  private mapProfessionalCourse(course: any) {
    const item: Record<string, unknown> = {
      user_id: this.userId,
      education_type: 'professional-courses',
      degree: "professional-course",
      year_of_degree: this.toPayloadString(course?.year),
      institute_name: (course?.institute ?? '').toString().trim(),
      Title: this.toPayloadString(course?.title),
      Percentage: null,
      CompletedStatus: this.professionalCoursesEnabled() ? 1 : 0,
      is_active: true
    };

    this.appendIdIfExists(item, course?.id);
    return item;
  }

  private createEducationGroup(defaults?: { degree?: string }): FormGroup {
    return this.fb.group({
      id: [null],
      completed: [false],
      degree: [defaults?.degree ?? '', [Validators.maxLength(this.degreeMaxLength)]],
      percentage: ['', this.optionalPercentageValidators],
      year_of_degree: ['', this.optionalYearValidators],
      institute_name: ['', [Validators.minLength(2), Validators.maxLength(this.instituteMaxLength)]]
    });
  }

  private createProfessionalCourse(value?: any): FormGroup {
    const group = this.fb.group({
      id: [value?.id ?? null],
      title: [value?.title ?? '', [Validators.maxLength(this.titleMaxLength)]],
      year: [value?.year ?? '', this.optionalYearValidators],
      institute: [value?.institute ?? '', [Validators.maxLength(this.instituteMaxLength)]]
    });
    if (this.educationForm) {
      this.applyProfessionalCourseCompletedValidators(group);
    }
    return group;
  }

  private appendIdIfExists(item: Record<string, unknown>, id: unknown) {
    if (id !== null && id !== undefined && id !== '') {
      item['id'] = id;
    }
  }

  private isCompleteStandardSection(sectionValue: any, requireDegree = false): boolean {
    // Requirements for standard sections (10th/12th):
    // - percentage must be non-empty
    // - year_of_degree must be non-empty
    // - institute_name must be non-empty
    const percentage = this.toPayloadString(sectionValue?.percentage);
    const year = this.toPayloadString(sectionValue?.year_of_degree);
    const institute = this.toPayloadString(sectionValue?.institute_name);
    const degree = this.toPayloadString(sectionValue?.degree);

    return !!sectionValue?.completed && !!(percentage && year && institute && (!requireDegree || degree));
  }

  private hasFormEducationData(education: any, options?: { defaultDegree?: string }): boolean {
    if (education?.id) {
      return true;
    }

    const hasCommonFields = !!(
      this.toPayloadString(education?.percentage) ||
      this.toPayloadString(education?.year_of_degree) ||
      (education?.institute_name ?? '').toString().trim()
    );

    if (hasCommonFields) {
      return true;
    }

    const degree = (education?.degree ?? '').toString().trim();
    if (!degree) {
      return false;
    }

    if (options?.defaultDegree && degree === options.defaultDegree) {
      return false;
    }

    return true;
  }


  private isCompleteProfessionalCourseSection(courseValue: any): boolean {
    // Professional course fields (all-or-nothing):
    // - title
    // - year
    // - institute
    // (id alone should not force inclusion when fields are empty)
    const title = this.toPayloadString(courseValue?.title);
    const year = this.toPayloadString(courseValue?.year);
    const institute = this.toPayloadString(courseValue?.institute);
    return this.professionalCoursesEnabled() && !!(title && year && institute);
  }

  private hasProfessionalCourseData(course: any): boolean {
    return !!(
      this.toPayloadString(course?.title) ||
      this.toPayloadString(course?.year) ||
      (course?.institute ?? '').toString().trim() ||
      course?.id
    );
  }


  private isEducationFormValid(): boolean {
    const standardSections = [this.tenth, this.twelfth, this.graduation, this.postGraduation];
    if (standardSections.some((section) => section.invalid)) {
      return false;
    }

    return this.areProfessionalCoursesValid();
  }

  private toPayloadString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return value.toString().trim();
  }

  private get optionalPercentageValidators() {
    return [Validators.min(0), Validators.max(100)];
  }

  private get optionalYearValidators() {
    return [Validators.min(this.yearMin), Validators.max(this.currentYear)];
  }

  private configureStandardCompletedToggles() {
    [
      { section: 'tenth', group: this.tenth },
      { section: 'twelfth', group: this.twelfth },
      { section: 'graduation', group: this.graduation },
      { section: 'postGraduation', group: this.postGraduation }
    ].forEach(({ section, group }) => {
      this.applyStandardCompletedValidators(group, this.requiresDegree(section));
      group.get('completed')?.valueChanges.subscribe(() => {
        this.applyStandardCompletedValidators(group, this.requiresDegree(section));
        if (!group.get('completed')?.value && this.openSection === section) {
          this.openSection = null;
        }
      });
    });
  }

  private configureProfessionalCoursesCompletedToggle() {
    this.applyProfessionalCoursesCompletedValidators();
    this.professionalCoursesCompleted?.valueChanges.subscribe(() => {
      this.applyProfessionalCoursesCompletedValidators();
      if (!this.professionalCoursesEnabled() && this.openSection === 'professionalCourses') {
        this.openSection = null;
      }
    });
  }

  private applyStandardCompletedValidators(group: FormGroup, requireDegree: boolean) {
    const isCompleted = !!group.get('completed')?.value;
    const controlNames = ['degree', 'percentage', 'year_of_degree', 'institute_name'];

    group.get('degree')?.setValidators([
      ...(isCompleted && requireDegree ? [Validators.required] : []),
      Validators.maxLength(this.degreeMaxLength)
    ]);
    group.get('percentage')?.setValidators([
      ...(isCompleted ? [Validators.required] : []),
      ...this.optionalPercentageValidators
    ]);
    group.get('year_of_degree')?.setValidators([
      ...(isCompleted ? [Validators.required] : []),
      ...this.optionalYearValidators
    ]);
    group.get('institute_name')?.setValidators([
      ...(isCompleted ? [Validators.required] : []),
      Validators.minLength(2),
      Validators.maxLength(this.instituteMaxLength)
    ]);

    controlNames.forEach((controlName) => {
      const control = group.get(controlName);
      if (isCompleted) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    });

    controlNames.forEach((controlName) => {
      group.get(controlName)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private applyProfessionalCourseCompletedValidators(group: FormGroup) {
    const isCompleted = this.professionalCoursesEnabled();
    const controlNames = ['title', 'year', 'institute'];

    group.get('title')?.setValidators([
      ...(isCompleted ? [Validators.required] : []),
      Validators.maxLength(this.titleMaxLength)
    ]);
    group.get('year')?.setValidators([
      ...(isCompleted ? [Validators.required] : []),
      ...this.optionalYearValidators
    ]);
    group.get('institute')?.setValidators([
      ...(isCompleted ? [Validators.required] : []),
      Validators.maxLength(this.instituteMaxLength)
    ]);

    controlNames.forEach((controlName) => {
      const control = group.get(controlName);
      if (isCompleted) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    });

    controlNames.forEach((controlName) => {
      group.get(controlName)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private getStandardSection(section: string): FormGroup | null {
    const sections: Record<string, FormGroup> = {
      tenth: this.tenth,
      twelfth: this.twelfth,
      graduation: this.graduation,
      postGraduation: this.postGraduation
    };

    return sections[section] ?? null;
  }

  private requiresDegree(section: string): boolean {
    return section === 'graduation' || section === 'postGraduation';
  }

  private getValidationErrorMessage(): string {
    const fields = [
      { group: this.tenth, label: 'Percentage', controlName: 'percentage', type: 'percentage' },
      { group: this.tenth, label: 'Year of Passing', controlName: 'year_of_degree', type: 'year' },
      { group: this.tenth, label: 'Institute', controlName: 'institute_name', type: 'text' },
      { group: this.twelfth, label: 'Percentage', controlName: 'percentage', type: 'percentage' },
      { group: this.twelfth, label: 'Year of Passing', controlName: 'year_of_degree', type: 'year' },
      { group: this.twelfth, label: 'Institute', controlName: 'institute_name', type: 'text' },
      { group: this.graduation, label: 'Degree', controlName: 'degree', type: 'text' },
      { group: this.graduation, label: 'Percentage', controlName: 'percentage', type: 'percentage' },
      { group: this.graduation, label: 'Year of Graduation', controlName: 'year_of_degree', type: 'year' },
      { group: this.graduation, label: 'Institute', controlName: 'institute_name', type: 'text' },
      { group: this.postGraduation, label: 'Degree', controlName: 'degree', type: 'text' },
      { group: this.postGraduation, label: 'Percentage', controlName: 'percentage', type: 'percentage' },
      { group: this.postGraduation, label: 'Year of Graduation', controlName: 'year_of_degree', type: 'year' },
      { group: this.postGraduation, label: 'Institute', controlName: 'institute_name', type: 'text' }
    ];

    for (const field of fields) {
      const control = field.group.get(field.controlName);
      if (control?.invalid) {
        return this.getControlErrorMessage(control, field.label, field.type);
      }
    }

    if (this.professionalCoursesEnabled()) {
      for (const course of this.professionalCourses.controls) {
        for (const field of [
          { label: 'Title', controlName: 'title', type: 'text' },
          { label: 'Year', controlName: 'year', type: 'year' },
          { label: 'Institute', controlName: 'institute', type: 'text' }
        ]) {
          const control = course.get(field.controlName);
          if (control?.invalid) {
            return this.getControlErrorMessage(control, field.label, field.type);
          }
        }
      }
    }

    return 'Please correct the highlighted education fields.';
  }

  private getControlErrorMessage(control: AbstractControl, label: string, type: string): string {
    if (type === 'percentage') {
      return this.getPercentageError(control);
    }

    if (type === 'year') {
      return this.getYearError(control, label);
    }

    return this.getTextError(control, label);
  }

  private loadEducation(userId: number | string) {
    this.profileApi.getEducationByUserId(userId).subscribe({
      next: (res: any) => {
        this.patchEducation(this.extractEducationList(res));
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Unable to load education details');
      }
    });
  }

  private patchEducation(educationList: any[]) {
    const professionalCourses = educationList.filter(education => this.isProfessionalCourse(education));

    educationList.forEach(education => {
      if (this.isProfessionalCourse(education)) {
        return;
      }

      const educationType = this.toEducationType(education);
      const value = {
        id: education?.id ?? null,
        completed: this.toCompletedBoolean(education),
        percentage: this.toInputValue(education?.Percentage ?? education?.percentage),
        degree: this.toInputValue(education?.degree),
        year_of_degree: this.toInputValue(education?.year_of_degree),
        institute_name: this.toInputValue(education?.institute_name ?? education?.institute)
      };

      if (educationType === 'ssc' || educationType === '10th') {
        this.tenth.patchValue(value);
      } else if (educationType === 'hsc' || educationType === '12th') {
        this.twelfth.patchValue(value);
      } else if (educationType === 'graduation') {
        this.graduation.patchValue(value);
      } else if (educationType === 'post-graduation') {
        this.postGraduation.patchValue(value);
      }
    });

    this.professionalCourses.clear();

    if (professionalCourses.length) {
      professionalCourses.forEach((course) => {
        this.professionalCourses.push(
          this.createProfessionalCourse(this.toProfessionalCourseFormValue(course))
        );
      });
    } else {
      this.professionalCourses.push(this.createProfessionalCourse());
    }

    this.ensureProfessionalCoursesEnabledFromCourses();
    this.professionalCourses.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  private extractEducationList(res: any): any[] {
    const candidates = [
      res?.data?.education,
      res?.data?.educations,
      res?.data,
      res?.education,
      res
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private toProfessionalCourseFormValue(course: any) {
    const titleFromApi =
      this.toInputValue(course?.Title ?? course?.title) ||
      this.toInputValue(course?.degree);

    return {
      id: course?.id ?? null,
      title: titleFromApi,
      year: this.toInputValue(course?.year_of_degree),
      institute: this.toInputValue(course?.institute_name ?? course?.institute)
    };
  }

  private isProfessionalCourse(education: any): boolean {
    const type = this.toEducationType(education);
    return (
      type.includes('professional') ||
      ['professional-course', 'professional-courses', 'professional course', 'professional_course'].includes(type)
    );
  }

  private toEducationType(education: any): string {
    return education?.education_type?.toString().trim().toLowerCase() || '';
  }

  private toCompletedBoolean(education: any): boolean {
    return education?.CompletedStatus === 1 || education?.CompletedStatus === '1' || education?.completed_status === 1 || education?.completed_status === '1' || education?.completed === true;
  }

  private toNumberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private toInputValue(value: any): any {
    if (value === null || value === undefined || value?.toString().trim().toLowerCase() === 'null') {
      return '';
    }

    return value;
  }

  private getUserIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }
}
